<template>
  <v-dialog
    scrollable
    :value="value"
    :max-width="isMobile() ? '' : '25%'"
    @click:outside="$emit('close')"
    @keydown.esc="$emit('close')"
  >
    <v-card elevation="1">
      <v-card-title>
        {{ $t('titles.timeDetails') }}
        <v-spacer/>
        <v-btn icon @click="$emit('close')">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider/>

      <v-card-text class="ma-0 pa-0">
        <v-container>
          <v-simple-table>
            <tbody>
            <tr
              v-for="detail in displayedDetails"
              :key="'details-row--' + detail.name"
            >
              <td class="text-left">{{ detail.name }}</td>
              <td class="text-right">
                <v-badge
                  :value="detail.updated"
                  color="primary"
                  inline
                  text
                  dot
                  left
                >
                  <span v-if="detail.value.isAbsence">{{ detail.value.absenceType }}</span>
                  <ApprovalStatusTableCell
                    v-else-if="detail.name === $t('tableHeaders.status')"
                    :item="detail.value"
                  />
                  <span v-else>{{ detail.value }}</span>
                </v-badge>
              </td>
            </tr>
            </tbody>
          </v-simple-table>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import find from 'lodash/find';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import concat from 'lodash/concat';

import constants from '@/constants';
import DateTimeUtil from '@/util/date-time-util';

import attachMobileBackButton from '@/mixins/attachMobileBackButton';
import hasTimePairFeatures from '@/mixins/timepairs/hasTimePairFeatures';
import isMobile from '@/mixins/displayAdaptable';

import ApprovalStatusTableCell from '@/components/timepairs/shared/ApprovalStatusTableCell.vue';
import getActualStartOrEndTime from '@/mixins/timepairs/getActualStartOrEndTime';

export default {
  name: 'TimePairDetailsDialog',

  components: { ApprovalStatusTableCell },

  mixins: [
    attachMobileBackButton,
    hasTimePairFeatures,
    isMobile,
    getActualStartOrEndTime,
  ],

  props: {
    value: Boolean,
    timePair: {
      type: Object,
      required: true,
    },
    previousVersion: null,
  },

  data() {
    return {
      timePairDetails: [],
      utilizedPreviousVersion: {},
    };
  },

  computed: {
    displayedDetails() {
      return filter(this.timePairDetails, (detail) => (
        detail.name !== 'Status' || this.isTimePairApprovalFeatureActive));
    },
  },

  watch: {
    timePair(value) {
      this.setTimePairDetails(value);
    },

    previousVersion(value) {
      this.utilizedPreviousVersion = value.timePairSummary || value;
      this.setTimePairDetails(this.timePair);
    },
  },

  methods: {
    setTimePairDetails(timePair) {
      if (isEmpty(timePair)) {
        this.timePairDetails = [];
        this.utilizedPreviousVersion = {};
        return;
      }

      const timePairDetails = this.getStandardFieldsDetails(timePair);
      const customFieldDetails = this.getCustomFieldsDetails(
        timePair.timePairSummary ? timePair.timePairSummary : timePair,
      );

      this.timePairDetails = concat(timePairDetails, customFieldDetails);
    },

    getStandardFieldsDetails(timePair) {
      const details = [];
      const isCorrectionSummary = !!timePair.timePairSummary;
      const valuesTimePair = isCorrectionSummary ? timePair.timePairSummary : timePair;

      if (timePair.correctionDate) {
        details.push(this.getDetailsObject(
          'Correction Date',
          DateTimeUtil.convertToHumanReadableDateTime(timePair.correctionDate),
          false,
        ));

        details.push(this.getDetailsObject(
          'Reason',
          isCorrectionSummary ? timePair.reason.description : '-',
          false,
        ));
      }

      details.push(this.getDetailsObject(
        'Position',
        valuesTimePair.employeeAssignment.position.description,
        !this.isAssignmentEqual(valuesTimePair.employeeAssignment.id),
      ));

      details.push(this.getDetailsObject(
        this.$t('tableHeaders.startDate'),
        DateTimeUtil.convertToHumanReadableDate(valuesTimePair.clockIn.date),
        !this.isClockInDateEqual(valuesTimePair.clockIn.date),
      ));

      details.push(this.getDetailsObject(
        this.$t('clock.clockIn'),
        DateTimeUtil.convertDateTimeToHumanReadableTime(this.getActualStartTime(valuesTimePair).date),
        !this.isClockInEqual(this.getActualStartTime(valuesTimePair).date),
      ));

      details.push(this.getDetailsObject(
        this.$t('clock.clockOut'),
        DateTimeUtil.convertToHumanReadableClockOutTime(
          this.getActualStartTime(valuesTimePair).date, this.getActualEndTime(valuesTimePair).date,
        ),
        !this.isClockOutEqual(valuesTimePair.clockOut.date),
      ));

      details.push(this.getDetailsObject(
        this.$t('tableHeaders.duration'),
        DateTimeUtil.getFormattedDuration(valuesTimePair.roundedDuration),
        false,
      ));

      details.push(this.getDetailsObject(
        this.$t('tableHeaders.status'),
        valuesTimePair,
        false,
      ));

      return details;
    },

    getCustomFieldsDetails(timePair) {
      const details = [];

      forEach(timePair.employeeAssignment.timePairCustomFields, (field) => {
        const fieldValue = this.getCustomFieldValue(timePair.timePairCustomFieldValues, field.id);
        if (fieldValue !== undefined) {
          const associatedCustomFieldDetails = this.getAssociatedCustomFieldDetails(timePair, field, fieldValue);
          if (associatedCustomFieldDetails) {
            details.push(associatedCustomFieldDetails);
          }
        }
      });

      return details;
    },

    getAssociatedCustomFieldDetails(timePair, field, fieldValue) {
      const timePairCustomFieldValue = find(
        timePair.timePairCustomFieldValues,
        { timePairCustomField: { id: field.id } },
      );

      switch (field.type) {
        case constants.TimePairCustomField.Type.FLAG: {
          return this.getDetailsObject(
            field.name,
            timePairCustomFieldValue.name === 'true' ? this.$t('misc.yes') : this.$t('misc.no'),
            !this.isFlagEqual(fieldValue.name, field.id),
          );
        }
        case constants.TimePairCustomField.Type.SELECT:
        case constants.TimePairCustomField.Type.COMBOBOX: {
          if (timePairCustomFieldValue.name === '') {
            return null;
          }
          return this.getDetailsObject(
            field.name,
            timePairCustomFieldValue.name,
            !this.isSelectionEqual(fieldValue.id, field.id),
          );
        }
        default: {
          return null;
        }
      }
    },

    getDetailsObject(name, value, updated) {
      return {
        name,
        value,
        updated,
      };
    },

    getCustomFieldValue(customFieldValues, customFieldId) {
      return find(customFieldValues, { timePairCustomField: { id: customFieldId } });
    },

    isAssignmentEqual(assignmentId) {
      return isEmpty(this.utilizedPreviousVersion)
        || (assignmentId === this.utilizedPreviousVersion.employeeAssignment.id);
    },

    isClockInDateEqual(dateTime) {
      return isEmpty(this.utilizedPreviousVersion)
        || DateTimeUtil.convertDateTimeToDate(dateTime)
        === DateTimeUtil.convertDateTimeToDate(this.utilizedPreviousVersion.clockIn.date);
    },

    isClockInEqual(dateTime) {
      return isEmpty(this.utilizedPreviousVersion)
        || dateTime === this.utilizedPreviousVersion.clockIn.date;
    },

    isClockOutEqual(dateTime) {
      return isEmpty(this.utilizedPreviousVersion)
          || dateTime === this.utilizedPreviousVersion.clockOut.date;
    },

    isFlagEqual(flagValue, fieldId) {
      if (isEmpty(this.utilizedPreviousVersion)) return true;

      const previousField = this.getCustomFieldValue(
        this.utilizedPreviousVersion.timePairCustomFieldValues,
        fieldId,
      );

      return previousField ? previousField.name === flagValue : false;
    },

    isSelectionEqual(selectionId, fieldId) {
      if (isEmpty(this.utilizedPreviousVersion)) return true;

      const previousField = this.getCustomFieldValue(
        this.utilizedPreviousVersion.timePairCustomFieldValues,
        fieldId,
      );

      return previousField ? previousField.id === selectionId : false;
    },
  },
};
</script>
